import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// An EV charging post: a slim pedestal with a touchscreen and status light, a
// charge cable looping down from the top to a connector holstered on the side.
// The kerbside refuelling point for electric vehicles.
const BODY = "#458a5c";
const BODY_DK = "#37704a";
const SCREEN = "#2a6f9e";
const STATUS = "#3fae5a";
const CABLE = "#23232a";
const NOZZLE = "#3a3a3e";

function cableSeg(a: Vec3, b: Vec3): Part {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const len = Math.hypot(dx, dy, dz) || 1e-4;
  const mid: Vec3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
  const pitch = Math.acos(Math.max(-1, Math.min(1, dy / len)));
  const yaw = Math.atan2(dx, dz);
  return { geometry: new THREE.CylinderGeometry(0.018, 0.018, len, 6), color: CABLE, position: mid, rotation: [pitch, yaw, 0], eulerOrder: "YXZ" };
}

export const evChargingStation = defineModel({
  id: "ev-charging-station",
  batchId: "electric",
  name: "EV charging station",
  tags: ["prop", "electric", "power", "charging", "ev", "consumer"],
  summary: "A charging post with a screen, status light and connector.",
  footprint: 0.3,
  parts(): Part[] {
    return [
      // Base and pedestal.
      { geometry: new THREE.BoxGeometry(0.34, 0.06, 0.26), color: "#5a5a5e", position: [0, 0.03, 0] },
      { geometry: new THREE.BoxGeometry(0.24, 0.95, 0.18), color: BODY, position: [0, 0.53, 0] },
      { geometry: new THREE.BoxGeometry(0.26, 0.06, 0.2), color: BODY_DK, position: [0, 1.03, 0] },
      // Touchscreen and status light.
      { geometry: new THREE.BoxGeometry(0.16, 0.22, 0.02), color: SCREEN, position: [0, 0.72, 0.1] },
      { geometry: new THREE.BoxGeometry(0.18, 0.03, 0.02), color: STATUS, position: [0, 0.88, 0.1] },
      // Holster and connector on the side.
      { geometry: new THREE.BoxGeometry(0.06, 0.12, 0.08), color: BODY_DK, position: [0.14, 0.55, 0.04] },
      { geometry: new THREE.BoxGeometry(0.05, 0.1, 0.06), color: NOZZLE, position: [0.16, 0.5, 0.06] },
      // Charge cable looping from the top down to the holster.
      cableSeg([0.1, 0.98, 0.05], [0.24, 0.78, 0.08]),
      cableSeg([0.24, 0.78, 0.08], [0.16, 0.6, 0.06]),
    ];
  },
  animations: [],
});
